import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  ArrowRight,
  Plug,
  Copy,
  Check,
  ShieldCheck,
  Zap,
  Bot,
} from 'lucide-react';

// Public MCP endpoint URL. REACT_APP_BACKEND_URL covers dev (localhost)
// and prod (split-domain: api.busybee.day) without code changes. Frontend
// is at busybee.day, backend / MCP server at api.busybee.day.
const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const BACKEND_URL = (RAW_BACKEND || 'https://api.busybee.day').replace(/\/$/, '');
const MCP_URL = `${BACKEND_URL}/mcp`;

// Tool inventory shown on the page. Matches what backend/routes/mcp_server.py
// exposes; backend/tests/test_bot_busybee_tools.py fails when the two differ.
const TOOL_GROUPS = [
  { key: 'tasks', tools: ['list_tasks', 'get_task', 'create_task', 'update_task', 'complete_task', 'delete_task', 'duplicate_task', 'list_scheduled_tasks', 'bulk_create_tasks', 'bulk_update_tasks', 'bulk_delete_tasks', 'reorder_tasks', 'move_tasks_to_section', 'add_task_attachment', 'read_task_attachment'] },
  { key: 'comments', tools: ['list_task_comments', 'add_task_comment', 'delete_comment'] },
  { key: 'projects', tools: ['list_projects', 'get_project', 'create_project', 'update_project', 'delete_project', 'list_project_sections', 'create_section', 'delete_section'] },
  { key: 'habits', tools: ['list_habits', 'create_habit', 'update_habit', 'delete_habit', 'complete_habit'] },
  { key: 'focus_time', tools: ['get_focus_time', 'update_focus_time'] },
  { key: 'labels', tools: ['list_labels', 'create_label', 'update_label', 'delete_label'] },
  { key: 'statuses', tools: ['list_task_statuses', 'create_task_status'] },
  { key: 'documents', tools: ['list_documents', 'get_document', 'create_document', 'update_document', 'delete_document', 'restore_document'] },
  { key: 'databases', tools: ['list_database_fields', 'create_database_field', 'update_database_field', 'delete_database_field', 'list_database_rows', 'create_database_rows', 'update_database_rows', 'delete_database_rows'] },
  { key: 'notifications', tools: ['list_notifications', 'mark_notification_read', 'mark_all_notifications_read'] },
  { key: 'search', tools: ['search'] },
  { key: 'settings', tools: ['get_settings', 'get_weekly_analytics', 'get_time_breakdown'] },
  { key: 'trash', tools: ['list_trash', 'restore_task', 'restore_project'] },
  { key: 'teams', tools: ['list_teams', 'get_team', 'list_team_tasks', 'list_team_members'] },
  { key: 'bots', tools: ['list_bots', 'create_bot', 'update_bot', 'delete_bot'] },
  { key: 'calendar', tools: ['list_calendar_events', 'create_calendar_event', 'delete_calendar_event'] },
  { key: 'planner', tools: ['auto_schedule_planner', 'reschedule_task', 'reschedule_tasks', 'suggest_times'] },
  { key: 'account', tools: ['whoami'] },
];

const TOTAL_TOOL_COUNT = TOOL_GROUPS.reduce((n, g) => n + g.tools.length, 0);

// Three-step recipe per supported client. Same as Higgsfield/Linear/Notion:
// the user pastes the MCP URL into their AI's Connector picker, then signs
// in to BusyBee in the OAuth popup. No tokens, no config files. The words of
// this page live in public/locales/<lang>/integrations.json under mcp.
const CLIENTS = ['claude', 'chatgpt', 'cursor', 'gemini', 'others'];

const STEPS = [
  { key: 0 },
  { key: 1, components: { b: <span className="text-amber-300" /> } },
  { key: 2, components: { em: <em /> } },
];
const CODE = <code className="px-1 mx-1 rounded bg-black/40 text-amber-300" />;

const UrlBox = ({ url, label }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked in iframe - silent */
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className="bg-[#0d0d0e] border border-gray-800 rounded-lg px-4 py-3 inline-flex items-center gap-3 font-mono text-sm hover:border-amber-500/50 transition-colors"
      aria-label={label}
    >
      <span className="text-amber-300 select-all">{url}</span>
      {copied ? (
        <Check className="w-4 h-4 text-amber-400 shrink-0" />
      ) : (
        <Copy className="w-4 h-4 text-gray-500 shrink-0" />
      )}
    </button>
  );
};

const McpIntegrationPage = () => {
  const { t, i18n } = useTranslation('integrations');
  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={t('mcp.seo_title')}
        description={t('mcp.seo_description')}
        canonical="/integrations/mcp"
      />
      <Navbar />

      <main className="pt-20 pb-24">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 py-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 mb-6">
            <Plug className="w-4 h-4" />
            <span className="text-sm font-medium">{t('mcp.badge')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('mcp.title')}
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            {t('mcp.subtitle')}
          </p>
          <div className="flex flex-col items-center gap-3">
            <span className="text-xs uppercase tracking-wider text-gray-500">
              {t('mcp.url_label')}
            </span>
            <UrlBox url={MCP_URL} label={t('mcp.copy_url')} />
          </div>
        </section>

        {/* Three-step recipe */}
        <section className="max-w-5xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-3 gap-4">
            {STEPS.map(({ key, components }) => (
              <div key={key} className="bg-[#1a1a1b] rounded-2xl border border-gray-800 p-6">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center font-bold mb-4">
                  {key + 1}
                </div>
                <h3 className="text-white font-semibold mb-2">
                  {t(`mcp.steps.${key}.title`)}
                </h3>
                <p className="text-sm text-gray-400">
                  <Trans t={t} ns="integrations" i18nKey={`mcp.steps.${key}.text`} components={components} />
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Quick facts */}
        <section className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-[#1a1a1b] rounded-xl border border-gray-800 p-5">
              <Zap className="w-6 h-6 text-amber-400 mb-2" />
              <h3 className="text-white font-semibold mb-1">
                {t('mcp.facts.setup.title')}
              </h3>
              <p className="text-sm text-gray-400">
                <Trans t={t} ns="integrations" i18nKey="mcp.facts.setup.text" components={{ code: CODE }} />
              </p>
            </div>
            <div className="bg-[#1a1a1b] rounded-xl border border-gray-800 p-5">
              <ShieldCheck className="w-6 h-6 text-amber-400 mb-2" />
              <h3 className="text-white font-semibold mb-1">
                {t('mcp.facts.control.title')}
              </h3>
              <p className="text-sm text-gray-400">
                <Trans t={t} ns="integrations" i18nKey="mcp.facts.control.text" components={{ code: CODE }} />
              </p>
            </div>
            <div className="bg-[#1a1a1b] rounded-xl border border-gray-800 p-5">
              <Bot className="w-6 h-6 text-amber-400 mb-2" />
              <h3 className="text-white font-semibold mb-1">
                {t('mcp.facts.tools.title', { count: TOTAL_TOOL_COUNT })}
              </h3>
              <p className="text-sm text-gray-400">
                {t('mcp.facts.tools.text')}
              </p>
            </div>
          </div>
        </section>

        {/* Where to find the connector picker for each client */}
        <section className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-white mb-2">{t('mcp.where.title')}</h2>
          <p className="text-gray-400 mb-6">
            {t('mcp.where.text')}
          </p>
          <div className="space-y-3">
            {CLIENTS.map((id) => (
              <div
                key={id}
                className="bg-[#1a1a1b] rounded-xl border border-gray-800 p-5 flex flex-wrap items-center gap-4"
              >
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-semibold">{t(`mcp.clients.${id}.name`)}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30">
                      {t(`mcp.clients.${id}.badge`)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400">{t(`mcp.clients.${id}.settings`)}</p>
                  {i18n.exists(`integrations:mcp.clients.${id}.note`) ? (
                    <p className="text-xs text-gray-500 mt-1">{t(`mcp.clients.${id}.note`)}</p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Tool inventory */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">
              {t('mcp.tools.title')}
            </h2>
            <span className="text-sm text-gray-500">
              {t('mcp.tools.summary', { count: TOTAL_TOOL_COUNT, groups: TOOL_GROUPS.length })}
            </span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {TOOL_GROUPS.map((group) => (
              <div
                key={group.key}
                className="bg-[#1a1a1b] rounded-xl border border-gray-800 p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold">{t(`mcp.tools.groups.${group.key}`)}</h3>
                  <span className="text-xs text-gray-500">
                    {group.tools.length}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {group.tools.map((tool) => (
                    <span
                      key={tool}
                      className="font-mono text-xs px-2 py-1 rounded bg-amber-500/5 border border-amber-500/20 text-amber-300"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Power users */}
        <section className="max-w-5xl mx-auto px-4 py-12">
          <div className="bg-[#1a1a1b] rounded-2xl border border-gray-800 p-8">
            <h2 className="text-2xl font-bold text-white mb-3">
              {t('mcp.tokens.title')}
            </h2>
            <p className="text-gray-400 mb-4">
              <Trans
                t={t}
                ns="integrations"
                i18nKey="mcp.tokens.text"
                components={{
                  code: CODE,
                  a: <Link to="/app/settings?tab=api" className="text-amber-400 hover:text-amber-300 mx-1 underline" />,
                }}
              />
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/app/settings?tab=api"
                className="bg-amber-500 text-black px-5 py-2.5 rounded-lg font-medium hover:bg-amber-400 transition-colors inline-flex items-center gap-2"
              >
                {t('mcp.tokens.mint')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/developers"
                className="border border-gray-700 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-white/5 transition-colors"
              >
                {t('mcp.tokens.developers')}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default McpIntegrationPage;
